<template>
  <div>
    <v-dialog v-model="show" max-width="1000px" :fullscreen="isMobile">
      <v-card class="checkin-dialog" tile>
        <v-toolbar
          style="height: auto;"
          elevation="0"
          class="py-3 toolbar-with-subheader"
          color="white"
        >
          <div class="toolbar-titles-container">
            <v-toolbar-title class="pl-5">Incheckning</v-toolbar-title>
            <v-toolbar-title class="pl-5 toolbar-subheader">{{ subTitle }}</v-toolbar-title>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon v-if="isMobile" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-menu
            v-else
            v-model="showSettingsMenu"
            :close-on-content-click="false"
            :nudge-width="200"
            offset-x
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                class="mr-1"
              >
                <v-icon>
                  mdi-tune
                </v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-list>
                <v-list-item>
                  <v-list-item-action>
                    <v-switch v-model="settingPrintCheckinConfirmationAutomatically" :disabled="!hasPrinter" color="secondary"></v-switch>
                  </v-list-item-action>
                  <v-list-item-title>Skriv ut incheckningsbekräftelse automatiskt</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </v-toolbar>
        <v-sheet
          class="d-flex overflow-y-auto"
          style="border-top: 1px solid lightgrey; min-height: 400px;"
        >
          <div
            v-if="isCheckingIn"
            class="pa-6 ma-auto d-flex flex-column align-self-stretch align-center"
            :class="{ 'd-block py-2': isMobile }"
          >
            <h3>Checkar in</h3>
            <v-progress-circular
              indeterminate
              size="48"
              class="my-4"
              color="secondary"
            ></v-progress-circular>
          </div>
          <div v-else style="width: 100%;" class="pa-4">
            <v-alert
              v-if="isCheckedIn"
              dense
              text
              type="success"
              class="d-block status-alert"
            >
              <div class="d-flex">
                <div class="flex-grow-1 my-auto">Bokningen har checkats in</div>
                <v-btn
                  outlined
                  color="success"
                  class="ml-auto"
                  @click="openBookingDialog"
                >Öppna bokning</v-btn>
              </div>
            </v-alert>
            <v-alert
              v-else-if="hasError"
              dense
              text
              type="error"
              class="d-block status-alert"
            >
              <div class="d-flex">
                <div class="flex-grow-1 my-auto">{{ checkInErrorMessage }}</div>
                <v-btn
                  outlined
                  color="error"
                  class="ml-auto"
                  @click="openBookingDialog"
                >
                  Öppna bokning
                </v-btn>
                <v-btn
                  v-show="checkinErrorCode === 'position_still_cleaning'"
                  text
                  color="error"
                  class="ml-2"
                  @click="checkInWithoutCleaningCheck"
                >
                  Checka in ändå
                </v-btn>
              </div>
            </v-alert>

            <v-row>
              <v-col>
                <v-card flat outlined>
                  <booking-data-list
                    :is-loading="!booking"
                    :booking="booking"
                    inside-dialog
                    :is-open="show"
                    :hidden-sections="['cancellation-policy', 'booking', 'channel']"
                    customer-clickable
                    position-clickable
                    allow-reverse-checkin
                    allow-reverse-checkout
                  ></booking-data-list>
                </v-card>
                <v-card flat class="mt-4">
                  <booking-input-remarks
                    :is-open="show"
                    :booking-remarks="booking && booking.remarks"
                    :booking-id="booking && booking.id"
                    @remarks-updated="remarksUpdated"
                  ></booking-input-remarks>
                </v-card>
              </v-col>
              <v-col>
                <v-alert
                  :type="(!booking?.has_unpaid_invoices && booking?.is_fully_paid) ? 'success' : 'warning'"
                  class="d-block status-alert"
                  text
                >
                  <div class="d-flex">
                    <div class="flex-grow-1 my-auto">
                      <template v-if="!booking?.is_fully_paid">
                        Bokningen är obetald
                      </template>
                      <template v-else-if="booking?.has_unpaid_invoices">
                        Bokningen har obetalda fakturor
                      </template>
                      <template v-else>
                        Bokningen är betald
                      </template>
                    </div>
                    <v-btn
                      v-show="booking?.has_unpaid_invoices || !booking?.is_fully_paid"
                      @click="openBookingInRegister"
                      outlined
                      color="warning"
                      class="ml-auto"
                    >
                      Öppna i kassan
                    </v-btn>
                  </div>
                </v-alert>

                <template v-if="hasTally">
                  <tally-order
                    :booking="booking"
                    :is-open="show"
                    :is-loading="!booking"
                    default-tab="card-list"
                    class="mt-4"
                  ></tally-order>
                </template>
                <template v-else-if="hasCamptrac">
                  <camptrac-order
                    :booking="booking"
                    :is-open="show"
                    :is-loading="!booking"
                    activate-reader-on-mount
                    class="mt-4"
                  ></camptrac-order>
                </template>
              </v-col>
            </v-row>
          </div>
        </v-sheet>
        <template v-if="showPrintRegistrationCardButton || showPrintCheckinConfirmationButton">
          <v-card-actions class="py-4">
            <v-btn
              v-if="showPrintRegistrationCardButton"
              @click="printRegistrationCard"
              text
              color="secondary"
            >
              Skriv ut registreringskort
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              v-if="showPrintCheckinConfirmationButton"
              @click="printCheckinConfirmation"
              :disabled="isCreatingTallyProvision"
              text
              color="success"
            >
              Skriv ut incheckningsbekräftelse
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import BookingService from '@/services/BookingService'

import BookingDataList from './DataList.vue'
import BookingInputRemarks from './InputRemarks.vue'
import TallyOrder from '@/components/tally/Order.vue'
import CamptracOrder from '@/components/camptrac/Order.vue'

export default {
  name: 'CheckinDialog',
  components: {
    BookingDataList,
    BookingInputRemarks,
    TallyOrder,
    CamptracOrder
  },
  data: () => ({
    isPrintingCheckinConfirmation: false,
    showSettingsMenu: false
  }),
  computed: {
    ...mapState(
      {
        booking: state => state.booking.checkinDialog.booking,
        checkinDate: state => state.booking.checkinDialog.checkinDate, // Användaren kan ha valt ett incheckningsdatum vid sen incheckning
        isCreatingProvisionBookingIds: state => state.tally.isCreatingProvisionBookingIds,
        bookingId: state => state.booking.checkinDialog.bookingId, // Denna finns redan före booking har hämtats
        isCheckingInBookingIds: state => state.booking.isCheckingInBookingIds,
        checkInErrorMessage: state => state.booking.checkinDialog.errorMessage,
        checkinErrorCode: state => state.booking.checkinDialog.errorCode,
        defaultPrinter: state => state.workstation?.default_printer,
        accessDriver: state => state.workstation?.access_driver
      }
    ),
    show: {
      get () {
        return this.$store.state.booking.checkinDialog.show
      },
      set (value) {
        this.$store.commit('booking/SET_SHOW_CHECKIN_DIALOG', value)
      }
    },
    settingPrintCheckinConfirmationAutomatically: {
      get () {
        return this.hasPrinter && this.$store.state.localSettings.printCheckinConfirmationAutomatically
      },
      set (value) {
        if (!this.hasPrinter) {
          return
        }
        this.$store.commit('localSettings/SET_PRINT_CHECKIN_CONFIRMATION_AUTOMATICALLY', value)
      }
    },
    hasTally: function () {
      return this.accessDriver === 'tally'
    },
    hasCamptrac: function () {
      return this.accessDriver === 'camptrac'
    },
    isCreatingTallyProvision: function () {
      return !!this.booking && this.isCreatingProvisionBookingIds.includes(this.booking.id)
    },
    hasPrinter: function () {
      return !!this.defaultPrinter
    },
    hasError: function () {
      return this.checkInErrorMessage !== ''
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    subTitle: function () {
      if (!this.booking) {
        return ''
      }
      return `${this.booking.title}, ${this.booking.subtitle}`
    },
    isCheckingIn: function () {
      return this.isCheckingInBookingIds.includes(this.bookingId)
    },
    isCheckedIn: function () {
      return this.booking && this.booking.checked_in_at != null
    },
    isSeasonBooking: function () {
      return this.booking && this.booking.type === 'season'
    },
    showPrintRegistrationCardButton: function () {
      return this.hasPrinter && this.booking && this.booking.checked_in_at && this.booking.customer.country.iso_code !== 'SE'
    },
    showPrintCheckinConfirmationButton: function () {
      return this.hasPrinter && this.booking && this.booking.checked_in_at
    }
  },
  methods: {
    remarksUpdated: function ({ bookingId, remarks }) {
      if (this.booking && this.booking.id === bookingId) {
        this.booking.remarks = remarks
      }
    },
    close: function () {
      this.show = false
    },
    checkInWithoutCleaningCheck: function () {
      this.close()
      this.$store.dispatch('booking/checkIn', { booking: this.booking, checkinDate: this.checkinDate, skipCleaningCheck: true })
    },
    openBookingDialog: function () {
      this.$store.dispatch('booking/openDialogById', { bookingId: this.booking.id })
      this.close()
    },
    openBookingInRegister: function () {
      this.$router.push({ name: 'PosRegister', params: { chargable: 'booking', id: this.booking.id } })
      this.close() // behövs för om detta är en booking overlay i en customer dialog så stängs den inte med closeDialogs, så den är öppen nästa gång customer dialog öppnas
    },
    printCheckinConfirmation: function () {
      // Ska använda pinkod och CustomInformation så det skrivs ut från server
      // Knappen är disabled till tally provision skapats, så server ska inte behöva vänta in detta
      this.isPrintingCheckinConfirmation = true
      BookingService.printCheckinConfirmation({ bookingId: this.booking.id })
        .finally(() => {
          this.isPrintingCheckinConfirmation = false
        })
    },
    printRegistrationCard: function () {
      const printObject = {
        content: []
      }
      printObject.content.push({
        type: 'text',
        alignment: 'center',
        value: 'Registration Card',
        text_size: 1,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'text',
        alignment: 'center',
        value: '  This data is collected as mandated by Swedish law 6 kap. 13§ Utlänningsförordningen (2006:97)', // 2 mellanslag i början är avsiktligt för att radbrytningen blir snyggare på 48 tecken bredd
        text_size: 0,
        bold: false
      })

      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'newline'
      })

      const givenName = this.booking.customer.given_name ?? '_________________'
      const surname = this.booking.customer.surname ?? '_________________'
      const dateOfArrival = this.booking.checked_in_at.format('YYYY-MM-DD')
      const dateOfDeparture = this.booking.check_out.format('YYYY-MM-DD')
      const addressLineOne = `${this.booking.customer.street_name ?? '___________________'} ${this.booking.customer.street_number ?? '______'}`
      const addressLineTwo = `${this.booking.customer.zip ?? '________'}, ${this.booking.customer.city ?? '________________'}, ${this.booking.customer.country.name_en}`

      printObject.content.push({
        type: 'text',
        value: 'Filled by guest',
        text_size: 0,
        alignment: 'center',
        bold: true
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Given name:', givenName],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Surname:', surname],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Date of birth:', '_________-_____-_____'],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Address:', addressLineOne],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'text',
        value: addressLineTwo,
        alignment: 'right',
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Date of arrival:', dateOfArrival],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Date of departure:', dateOfDeparture],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Signature:', `____________________ ${window.dayjs.today().format('YYYY-MM-DD')}`],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'newline'
      })

      printObject.content.push({
        type: 'line'
      })
      printObject.content.push({
        type: 'newline'
      })

      printObject.content.push({
        type: 'text',
        value: 'Filled by staff',
        text_size: 0,
        alignment: 'center',
        bold: true
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['ID Type:', '__ PASSPORT  __ ID-CARD  __ OTHER'],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'left-right',
        value: ['Document number:', '__________________'],
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'newline'
      })
      printObject.content.push({
        type: 'text',
        value: 'The card should be saved at least until the following date in accordance with PMFS 2015:6',
        text_size: 0,
        bold: false
      })
      printObject.content.push({
        type: 'text',
        value: window.dayjs.today().add(3, 'months').format('YYYY-MM-DD'),
        text_size: 0,
        bold: true
      })
      printObject.content.push({
        type: 'newline'
      })

      /* efternamn, förnamn, födelsetid, hemortsadress, ankomstdag och avresedag */
      /* vilken legitimationshandling som uppvisats, handlingens nummer samt utlänningens underskrift och datum för denna */
      /* Kortet ska sparas i tre månader från den dag utlänningen registrerades hos den registreringsansvarige */

      printObject.content.push({
        type: 'cut'
      })
      this.$store.dispatch('print/print', printObject)
    }
  },
  watch: {
    isCheckingIn: function (newVal, oldVal) {
      if (!newVal && oldVal && this.isCheckedIn && !this.isCreatingTallyProvision && this.settingPrintCheckinConfirmationAutomatically) {
        // Automatisk utskrift av incheckningsbekräftelse om inställning aktiverad, bokning incheckad och tallyprovision skapas inte
        this.printCheckinConfirmation()
      }
    },
    isCreatingTallyProvision: function (newVal, oldVal) {
      console.log('isCreatingTallyProvision changed', oldVal, newVal)
      if (oldVal && !newVal && this.isCheckedIn && this.settingPrintCheckinConfirmationAutomatically) {
        // Automatisk utskrift av incheckningsbekräftelse om inställning aktiverad, bokning incheckad och tallyprovision skapad
        this.printCheckinConfirmation()
      }
    }
  }
}
</script>
